/*
 * Level (L1/L2/L3) and scope vocabulary for the sales task list. The caller's
 * own level comes from GET /v2/sales_team/me; the list never persists the
 * selected level, so each visit starts from the caller's level.
 */

import { hasAccess } from '../../../util_functions';
import type { RepLevel, SalesMe } from '../sales_team/api';

export type LevelFilter = RepLevel | 'ALL';
export type Scope = 'all' | 'unclaimed' | 'mine' | 'my_students';

export interface Option<V extends string = string> {
  value: V;
  label: string;
}

export const LEVELS: RepLevel[] = ['L1', 'L2', 'L3'];
export const ALL_LEVELS = 'ALL';
export const FALLBACK_LEVEL: RepLevel = 'L1';
export const SCOPE_VALUES: Scope[] = ['all', 'unclaimed', 'mine', 'my_students'];

const is_rep_level = (v: unknown): v is RepLevel =>
  typeof v === 'string' && (LEVELS as string[]).includes(v);

// The server's capability flags decide; a MANAGER row without them is not one.
export const is_manager = (me: SalesMe | null | undefined): boolean =>
  !!me?.can_assign || !!me?.can_edit_team;

// MANAGER, a non-member (null) and anything unrecognised resolve to null.
export const level_from_me = (me: SalesMe | null | undefined): RepLevel | null =>
  is_rep_level(me?.level) ? me.level : null;

export const default_level = (me: SalesMe | null | undefined): LevelFilter =>
  is_manager(me) ? ALL_LEVELS : level_from_me(me) || FALLBACK_LEVEL;

// access_type only counts when /me failed (me is null).
const may_see_all = (me: SalesMe | null | undefined, access_type?: string): boolean =>
  me ? is_manager(me) : hasAccess(access_type, 'SUPER_USER');

export const level_options = (
  me: SalesMe | null | undefined,
  access_type?: string
): Option<LevelFilter>[] => {
  const mine = level_from_me(me);
  const options: Option<LevelFilter>[] = LEVELS.map((l) => ({
    value: l,
    label: l === mine ? `${l} (you)` : l
  }));
  if (may_see_all(me, access_type)) options.push({ value: ALL_LEVELS, label: 'All' });
  return options;
};

// A well-formed ?level=, not yet checked against the caller's options.
export const url_level = (search: string | null | undefined): LevelFilter | null => {
  const requested = new URLSearchParams(search || '').get('level');
  return is_rep_level(requested) || requested === ALL_LEVELS ? requested : null;
};

// ?level= wins when it is one of the caller's options.
export const resolve_level = (
  search: string | null | undefined,
  me: SalesMe | null | undefined,
  access_type?: string
): LevelFilter => {
  const requested = url_level(search);
  if (requested && level_options(me, access_type).some((o) => o.value === requested)) {
    return requested;
  }
  return default_level(me);
};

export const scope_options = (level: LevelFilter | null | undefined): Option<Scope>[] => {
  const options: Option<Scope>[] = [
    { value: 'all', label: 'All' },
    { value: 'unclaimed', label: 'Unclaimed' },
    { value: 'mine', label: 'Claimed by me' }
  ];
  if (level === 'L3') options.push({ value: 'my_students', label: 'My students' });
  return options;
};

export const normalize_scope = (
  scope: string | null | undefined,
  level: LevelFilter | null | undefined
): Scope => {
  const hit = scope_options(level).find((o) => o.value === scope);
  return hit ? hit.value : 'all';
};

export interface PersonColumn {
  label: string;
  sort_key: 'claimed' | 'owner';
}

export const person_column = (level: LevelFilter | null | undefined): PersonColumn => {
  if (level === 'L3') return { label: 'Owner', sort_key: 'owner' };
  if (level === ALL_LEVELS) return { label: 'Claimed by / Owner', sort_key: 'claimed' };
  return { label: 'Claimed by', sort_key: 'claimed' };
};

export const can_assign = (me: SalesMe | null | undefined, access_type?: string): boolean =>
  !!me?.can_assign || may_see_all(me, access_type);
